<template>
  <div>
    <merus-data-table
      :headers="headers"
      :items="variaveis"
      :loading="loading"
      :loading-text="$t('system.messages.loading')"
      :default-multi-sort="defaultSort"
      @update:displayedItems="displayedVariaveis = $event"
    >
      <template v-slot:toolbar>
        <cadastro-toolbar
          :title="$t('subsidyMotorCatalog.variables.title')"
          :token="token"
          :items="displayedVariaveis !== null ? displayedVariaveis : variaveis"
          :csv-title="$t('subsidyMotorCatalog.variables.listTitle')"
          :csv-headers="headers"
          hide-search
        >
          <template v-slot:add v-if="canInsert">
            <v-btn color="primary" dark small class="mb-2" fab @click="openNewVariavel">
              <v-icon>mdi-plus</v-icon>
            </v-btn>
          </template>
        </cadastro-toolbar>
      </template>
      <template v-slot:item.actions="{ item }">
        <v-icon color="green" dark small @click="editVariavel(item)" :disabled="!canEdit">mdi-pencil</v-icon>
        <v-icon color="red" dark small @click="deleteVariavel(item)" :disabled="!canDelete">mdi-delete</v-icon>
      </template>
    </merus-data-table>

    <v-dialog v-model="dialogVariavel" max-width="800px" persistent>
      <v-card>
        <v-card-title>{{ dialogVariavelTitle }}</v-card-title>
        <v-card-text>
          <v-form ref="formVariavel" v-model="formVariavelValidado">
            <v-row>
              <v-col cols="12" sm="4">
                <v-text-field
                  v-model="editedVariavel.codigo"
                  :label="$t('subsidyMotorCatalog.fields.code')"
                  :rules="[codigoRules]"
                  maxlength="50"
                  :disabled="editedVariavelIndex !== -1"
                />
              </v-col>
              <v-col cols="12" sm="8">
                <v-text-field v-model="editedVariavel.descricao" :label="$t('system.description')" :rules="[requiredRule]" />
              </v-col>
              <v-col cols="12" sm="4">
                <v-select
                  v-model="editedVariavel.fonte"
                  :items="fonteOptions"
                  item-text="text"
                  item-value="value"
                  :label="$t('subsidyMotorCatalog.fields.source')"
                  :rules="[requiredRule]"
                  @change="onFonteChange"
                />
              </v-col>
              <v-col cols="12" sm="4">
                <v-select
                  v-model="editedVariavel.campo_origem"
                  :items="campoOrigemOptions"
                  item-text="text"
                  item-value="value"
                  :label="$t('subsidyMotorCatalog.fields.originField')"
                  :rules="[requiredRule]"
                  :disabled="!editedVariavel.fonte"
                />
              </v-col>
              <v-col cols="12" sm="4">
                <v-select
                  v-model="editedVariavel.tipo_dado"
                  :items="tipoDadoOptions"
                  item-text="text"
                  item-value="value"
                  :label="$t('subsidyMotorCatalog.fields.dataType')"
                  :rules="[requiredRule]"
                />
              </v-col>
            </v-row>
          </v-form>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn color="primary" dark @click="saveVariavel">{{ $t("button.save") }}</v-btn>
          <v-btn text @click="closeVariavel">{{ $t("button.cancel") }}</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import MerusDataTable from "../../Tables/MerusDataTable.vue";
import CadastroToolbar from "../../Toolbars/CadastroToolbar.vue";
import apiController from "../../../utils/apiController";
import EventBus from "../../../utils/eventBus";
import { validador } from "@/constants";

const emptyVariavel = () => ({
  id_variavel: null,
  codigo: "",
  descricao: "",
  fonte: "grid_viagens",
  campo_origem: "km_elegivel",
  tipo_dado: "NUMERICO",
});

// Fora de `contrato`, espelha o registro do motor em
// api/utils/subsidioMotorVariaveis.js: um campo que não esteja lá não gera
// valor na materialização, então não é oferecido.
const CAMPOS_ORIGEM_POR_FONTE = {
  grid_viagens: [
    "km_elegivel",
    "km_aferido",
    "km_nominal",
    "delay_chegada_min",
    "delay_saida_min",
    "id_viagem_grid",
    "id_linha",
    "id_veiculo_realizado",
    "id_motorista_realizado",
    "id_veiculo_pico_manha",
    "id_veiculo_pico_tarde",
    "autorizado_prefeitura",
    "conciliacao_cancelada",
  ],
  posicoes: [
    "porcentagem_dentro_rota",
    "pct_posicoes_enviadas",
    "pct_cobertura_tempo",
    "numero_posicoes_enviadas",
    "minutos_com_posicao",
  ],
  pontualidade: [
    "atraso_chegada_min",
    "incidencia_atraso",
    "incidencia_teto",
    "incidencia_cobravel",
    "incidencia_cobravel_teto",
  ],
  periodo: ["frota_media", "dias_periodo", "fator_ciclo"],
  contrato: [
    "valor_km",
    "porcentagem_posicoes_rota",
    "porcentagem_posicoes_enviadas",
    "porcentagem_posicoes_tempo",
    "tempo_entre_posicoes",
    "tempo_limite_envio",
  ],
  // Ainda sem leitura no motor; mantida para editar variáveis já cadastradas.
  bilhetagens: [],
};

export default {
  components: {
    MerusDataTable,
    CadastroToolbar,
  },
  props: {
    variaveis: { type: Array, default: () => [] },
    loading: { type: Boolean, default: false },
    token: { type: String, default: null },
    canInsert: { type: Boolean, default: false },
    canEdit: { type: Boolean, default: false },
    canDelete: { type: Boolean, default: false },
    defaultSort: { type: Array, default: () => [] },
  },
  data: () => ({
    displayedVariaveis: null,
    dialogVariavel: false,
    formVariavelValidado: true,
    editedVariavelIndex: -1,
    editedVariavel: emptyVariavel(),
  }),
  computed: {
    headers() {
      return [
        { text: this.$t("subsidyMotorCatalog.fields.code"), value: "codigo", sortable: true, filtrable: true },
        { text: this.$t("system.description"), value: "descricao", sortable: true, filtrable: true },
        { text: this.$t("subsidyMotorCatalog.fields.source"), value: "fonte", sortable: true, filtrable: true },
        { text: this.$t("subsidyMotorCatalog.fields.originField"), value: "campo_origem", sortable: true, filtrable: true },
        { text: this.$t("subsidyMotorCatalog.fields.dataType"), value: "tipo_dado", sortable: true, filtrable: true },
        { text: this.$t("contract.table.headers.actions"), value: "actions", sortable: false, filtrable: false },
      ];
    },
    fonteOptions() {
      return [
        { value: "grid_viagens", text: this.$t("subsidyMotorCatalog.sources.gridViagens") },
        { value: "posicoes", text: this.$t("subsidyMotorCatalog.sources.posicoes") },
        { value: "pontualidade", text: this.$t("subsidyMotorCatalog.sources.pontualidade") },
        { value: "periodo", text: this.$t("subsidyMotorCatalog.sources.periodo") },
        { value: "contrato", text: this.$t("subsidyMotorCatalog.sources.contrato") },
        { value: "bilhetagens", text: this.$t("subsidyMotorCatalog.sources.bilhetagens") },
      ];
    },
    tipoDadoOptions() {
      return [
        { value: "NUMERICO", text: this.$t("subsidyMotorCatalog.dataTypes.numeric") },
        { value: "BOOLEANO", text: this.$t("subsidyMotorCatalog.dataTypes.boolean") },
        { value: "DURACAO", text: this.$t("subsidyMotorCatalog.dataTypes.duration") },
        { value: "PERCENTUAL", text: this.$t("subsidyMotorCatalog.dataTypes.percent") },
      ];
    },
    dialogVariavelTitle() {
      return this.editedVariavelIndex === -1
        ? this.$t("subsidyMotorCatalog.variables.new")
        : `${this.$t("button.edit")} — ${this.$t("subsidyMotorCatalog.tabs.variables")}`;
    },
    requiredRule() {
      return (v) => (!!v && String(v).trim().length > 0) || this.$t("system.messages.requiredField");
    },
    codigoRules() {
      if (!validador(this.editedVariavel.codigo, 1, 50, 0, 1)) {
        return this.$t("subsidyMotorCatalog.variables.invalidCode");
      }
      return true;
    },
    campoOrigemOptions() {
      return this.buildCampoOrigemOptions(this.editedVariavel.fonte, this.editedVariavel.campo_origem);
    },
  },
  methods: {
    openNewVariavel() {
      this.editedVariavelIndex = -1;
      this.editedVariavel = emptyVariavel();
      this.dialogVariavel = true;
    },
    buildCampoOrigemOptions(fonte, currentValue) {
      const keys = [...(CAMPOS_ORIGEM_POR_FONTE[fonte] || [])];
      if (currentValue && !keys.includes(currentValue)) {
        keys.unshift(currentValue);
      }
      return keys.map((value) => ({
        value,
        text: this.originFieldLabel(value),
      }));
    },
    originFieldLabel(value) {
      const key = `subsidyMotorCatalog.originFields.${value}`;
      const translated = this.$t(key);
      return translated !== key ? translated : value;
    },
    onFonteChange() {
      const options = this.buildCampoOrigemOptions(this.editedVariavel.fonte);
      const current = this.editedVariavel.campo_origem;
      if (!options.some((o) => o.value === current)) {
        this.editedVariavel.campo_origem = options.length ? options[0].value : "";
      }
    },
    editVariavel(item) {
      this.editedVariavelIndex = this.variaveis.indexOf(item);
      this.editedVariavel = { ...item };
      this.dialogVariavel = true;
    },
    closeVariavel() {
      this.dialogVariavel = false;
      this.editedVariavel = emptyVariavel();
      this.editedVariavelIndex = -1;
      if (this.$refs.formVariavel) this.$refs.formVariavel.resetValidation();
    },
    async saveVariavel() {
      if (this.$refs.formVariavel) this.$refs.formVariavel.validate();
      if (!this.formVariavelValidado) return;
      const endpoint =
        this.editedVariavelIndex === -1
          ? apiController.endpoints.subsidioMotorInsertVariavel
          : apiController.endpoints.subsidioMotorUpdateVariavel;
      try {
        const resp = await apiController.request(endpoint, { data: this.editedVariavel, silent: true });
        if (resp.data && resp.data.erro) {
          EventBus.$emit("show-alert", { mensagem: resp.data.mensagem || this.$t("system.unknownSystemError"), tipo: "error" });
          return;
        }
        EventBus.$emit("show-alert", { mensagem: this.$t("subsidyMotorCatalog.saved"), tipo: "success" });
        this.closeVariavel();
        this.$emit("refresh", ["variaveis"]);
      } catch (e) {
        EventBus.$emit("show-error", { error: e });
      }
    },
    async deleteVariavel(item) {
      try {
        await apiController.request(apiController.endpoints.subsidioMotorDeleteVariavel, {
          data: { id_variavel: item.id_variavel },
          silent: true,
        });
        this.$emit("refresh", ["variaveis"]);
      } catch (e) {
        EventBus.$emit("show-error", { error: e });
      }
    },
  },
};
</script>
